import styles from './multip-page-modal.module.css';
import { Dialog, DialogActions, DialogContent, makeStyles } from '@material-ui/core';
import { useMultiPageWizard } from './use-multi-page-wizard';
import { Button, Typography } from '@SBGSports/referee-react';
import { IconCancelSmall } from '@SBGSports/referee-react/icons';
import { Translator, useTranslations } from 'i18n';
import { useState } from 'react';
import { ButtonTypeAction, PathButtonAction } from './types';

const useStyles = makeStyles(() => ({
    customDialog: {
        maxWidth: '1167px', // Set custom max-width for the dialog
        margin: 'auto', // Center the dialog horizontally
    },
}));

interface MultiPageModalProps {
    isOpen: boolean;
    handleClose: (isOpen: boolean) => void;
}

const addDefaultButtons = (prevDetails: PathButtonAction[], details: PathButtonAction[]) => {
    const tempBtnDetails: PathButtonAction[] = details;
    prevDetails.forEach((item) => {
        if (
            details.every((btn) => btn.type !== item.type) &&
            details.every((btn) => btn.type !== ButtonTypeAction.cancel) &&
            details.every((btn) => btn.type !== ButtonTypeAction.revert)
        ) {
            tempBtnDetails.push(item);
        }
    });

    return tempBtnDetails;
};

export const defaultStartupBtns = (__: Translator, isDisabled = true) => [
    {
        type: ButtonTypeAction.advance,
        text: __('misc.get_started'),
        isDisabled: isDisabled,
    },
    {
        type: ButtonTypeAction.cancel,
        text: __('misc.confirm_no'),
    },
];

export const defaultNavigationBtns = (__: Translator, isDisabled = false) => [
    {
        type: ButtonTypeAction.advance,
        text: __('misc.next'),
        isDisabled: isDisabled,
    },
    {
        type: ButtonTypeAction.revert,
        text: __('misc.back'),
    },
];

export const MultiPageModal: React.FC<MultiPageModalProps> = (props) => {
    const { isOpen, handleClose } = props;
    const { __ } = useTranslations();
    const classes = useStyles();
    const { steps, currentStepIndex, header, next, back } = useMultiPageWizard();
    const [buttonDetails, setButtonDetails] = useState<PathButtonAction[]>(defaultStartupBtns(__));
    const Type = steps[currentStepIndex].type;

    const setButtonDetailsCallback = (details: PathButtonAction[]) => {
        const combineIncomingAndDefault = addDefaultButtons(buttonDetails, details);
        setButtonDetails(combineIncomingAndDefault);
    };

    return (
        <Dialog
            disableEscapeKeyDown
            fullWidth
            classes={{
                paper: classes.customDialog,
            }}
            open={isOpen}
            onClose={(_, reason) => {
                if (reason === 'backdropClick') {
                    return;
                }
                handleClose(false);
            }}
        >
            <div className={styles['dialog-title']}>
                <div>
                    {header}
                    {currentStepIndex > 0 && (
                        <Typography variant="heading-5">
                            {__('smt_importer.step_current_of_final', {
                                current: `${currentStepIndex}`,
                                final: `${steps.length - 1}`,
                            })}
                        </Typography>
                    )}
                </div>
                <Button onClick={() => handleClose(false)} title="cancel" size="small" variant="text">
                    <IconCancelSmall />
                </Button>
            </div>
            <DialogContent>
                <Type {...steps[currentStepIndex].props} setButtonDetails={setButtonDetailsCallback} />
            </DialogContent>
            <DialogActions>
                <div className={styles['dialog-footer']}>
                    {buttonDetails
                        .filter((button) => button.type !== ButtonTypeAction.advance)
                        .map((button, index) => (
                            <Button
                                key={index}
                                onClick={() => {
                                    if (button.type === ButtonTypeAction.cancel) {
                                        handleClose(false);
                                    } else {
                                        button.action?.() ?? back();
                                    }
                                }}
                                variant="text"
                            >
                                {button.text ?? __('misc.confirm_no')}
                            </Button>
                        ))}
                    <Button
                        disabled={buttonDetails.find((button) => button.type === ButtonTypeAction.advance)?.isDisabled}
                        onClick={() => {
                            buttonDetails.find((button) => button.type === ButtonTypeAction.advance)?.action?.() ??
                                next();
                        }}
                    >
                        {buttonDetails.find((button) => button.type === ButtonTypeAction.advance)?.text ??
                            __('misc.get_started')}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
};
